import * as z from "zod";
import { userFacingError } from "../authorization";
import { applyScopeGrantPatch } from "../rbac/applyScopeGrantPatch";
import { grantsForScope } from "../rbac/grantQueries";
import { fetchCustomRole } from "../rbac/grantResources";
import type { BuiltinRoleId } from "../rbac/roles";
import { assertValidSpaceDefaultJoinRole } from "../rbac/validation";
import { mutator } from "./mutator";

const joinSpaceSchema = z.object({
  spaceId: z.string(),
});

export const joinSpace = mutator({
  schema: joinSpaceSchema,
  permissions: {
    none: "self-service joining, gated in fn by the space’s default join role",
  },
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  fn: async ({ tx, ctx, args: { space } }) => {
    if (space.mode === "personal") {
      throw userFacingError("This space cannot be joined");
    }

    const builtinRoleId = space.defaultJoinBuiltinRoleId;
    const customRoleId = space.defaultJoinCustomRoleId;
    if (!builtinRoleId && !customRoleId) {
      throw userFacingError("This space is not discoverable");
    }

    const customRole = customRoleId
      ? await fetchCustomRole(tx, customRoleId, ctx.tenantId)
      : null;
    assertValidSpaceDefaultJoinRole({
      builtinRoleId: builtinRoleId as BuiltinRoleId | null,
      customRole,
    });

    const existingGrant = await tx.run(
      grantsForScope(space.id, ctx.tenantId).where("userId", ctx.userId)
    );
    if (existingGrant.length > 0) return;

    await applyScopeGrantPatch({
      tx,
      ctx,
      scopeId: space.id,
      scopeKind: "space",
      add: [
        {
          userId: ctx.userId,
          builtinRoleId: (builtinRoleId as BuiltinRoleId | null) ?? undefined,
          customRoleId: customRoleId ?? undefined,
        },
      ],
      remove: [],
      customRoles: customRole ? [customRole] : [],
    });
  },
});
